<div class="flex py-3">
  <div class="flex-1">
    <h1 class="text-3xl font-semibold">Send Communication</h1>
    <p class="text-lg text-gray-600">Send a template, or compose a freeform email/SMS.</p>
  </div>
  <div class="flex">
    <button
      pButton
      icon="pi pi-times"
      class="p-button-rounded p-button-danger mt-2 p-button-text"
      (click)="onCancel()"
    ></button>
  </div>
</div>

<div class="flex flex-column gap-3">
  <div class="grid align-items-end">
    <div class="col-12 md:col-4">
      <label class="block text-600 text-sm mb-1">Channel</label>
      <p-selectButton
        [options]="channelOptions"
        [(ngModel)]="channel"
        optionLabel="label"
        optionValue="value"
        (onChange)="onChannelChange()"
      ></p-selectButton>
    </div>
    <div class="col-12 md:col-4">
      <label class="block text-600 text-sm mb-1">Send As (Portal Tenant)</label>
      <p-dropdown
        [options]="portalTenants"
        optionLabel="name"
        optionValue="id"
        placeholder="Select a portal tenant"
        [(ngModel)]="portalTenantId"
        (onChange)="onPortalTenantChange()"
        appendTo="body"
        styleClass="w-full"
      ></p-dropdown>
    </div>
    <div class="col-12 md:col-4">
      <label class="block text-600 text-sm mb-1">Template (optional)</label>
      <p-dropdown
        [options]="templates"
        optionLabel="name"
        optionValue="template_key"
        placeholder="Freeform (no template)"
        [(ngModel)]="selectedTemplateKey"
        [showClear]="true"
        (onChange)="onTemplateSelect()"
        appendTo="body"
        styleClass="w-full"
      ></p-dropdown>
    </div>
  </div>

  <div>
    <label class="block text-600 text-sm mb-1">Recipient(s)</label>
    <input
      type="text"
      pInputText
      class="w-full"
      [class.surface-200]="recipientLocked"
      [readonly]="recipientLocked"
      [(ngModel)]="recipientsInput"
      [placeholder]="isEmail ? 'name@example.com, name2@example.com' : '0821234567, 0837654321'"
    />
    <small class="text-500" *ngIf="!lockRecipient">Separate multiple recipients with a comma.</small>
    <small class="text-orange-500" *ngIf="missingCustomerRecipient">
      This customer has no {{ isEmail ? 'email address' : 'mobile number' }} on file - enter one, or update their contact details.
    </small>
  </div>

  <!-- Templated send: the backend renders the template from these values. -->
  <ng-container *ngIf="isTemplatedSend; else contentFields">
    <div *ngIf="variableFields.length" class="grid">
      <div class="col-12 md:col-6" *ngFor="let field of variableFields">
        <label class="block text-600 text-sm mb-1">
          {{ field.key }} <span class="text-red-500" *ngIf="field.required">*</span>
        </label>
        <input
          type="text"
          pInputText
          class="w-full"
          [(ngModel)]="variableValues[field.key]"
          [placeholder]="field.placeholder"
        />
      </div>
    </div>

    <div class="flex gap-2">
      <p-button
        label="Preview"
        icon="pi pi-eye"
        styleClass="p-button-outlined p-button-sm"
        [loading]="previewLoading"
        (onClick)="renderPreview()"
      ></p-button>
      <p-button
        *ngIf="canSendFreeform"
        label="Edit content"
        icon="pi pi-pencil"
        styleClass="p-button-text p-button-sm"
        [disabled]="previewLoading"
        (onClick)="editTemplateContent()"
      ></p-button>
    </div>

    <div *ngIf="preview" class="flex flex-column gap-2">
      <div *ngIf="isEmail">
        <label class="block text-600 text-sm mb-1">Subject</label>
        <div class="surface-100 border-round p-2">{{ preview.subject }}</div>
      </div>
      <iframe
        *ngIf="isEmail && preview.body_html; else textPreview"
        [srcdoc]="preview.body_html"
        style="width: 100%; height: 20rem; border: 1px solid var(--surface-300); border-radius: 6px; background: white"
      ></iframe>
      <ng-template #textPreview>
        <div class="surface-100 border-round p-2" style="white-space: pre-wrap">{{ preview.body_text }}</div>
      </ng-template>
    </div>

    <small class="text-red-500" *ngIf="!canSendTemplated">
      You don't have permission to send templated communications.
    </small>
  </ng-container>

  <ng-template #contentFields>
    <div *ngIf="editingTemplate" class="surface-100 border-round p-2 text-sm flex align-items-center justify-content-between">
      <span>Editing the rendered template - this is sent as a freeform message.</span>
      <p-button
        label="Back to template"
        styleClass="p-button-text p-button-sm"
        (onClick)="editingTemplate = false"
      ></p-button>
    </div>
    <small class="text-red-500" *ngIf="!canSendFreeform">
      You don't have permission to send freeform messages - choose a template instead.
    </small>

    <ng-container *ngIf="isEmail; else smsFields">
      <div>
        <label class="block text-600 text-sm mb-1">Subject <span class="text-red-500">*</span></label>
        <input type="text" pInputText class="w-full" [(ngModel)]="subject" />
      </div>

      <div>
        <div class="flex align-items-center justify-content-between mb-1">
          <label class="block text-600 text-sm m-0">Body (HTML) <span class="text-red-500">*</span></label>
          <p-button
            [label]="showHtmlPreview ? 'Edit' : 'Preview'"
            [icon]="showHtmlPreview ? 'pi pi-pencil' : 'pi pi-eye'"
            styleClass="p-button-text p-button-sm"
            (onClick)="showHtmlPreview = !showHtmlPreview"
          ></p-button>
        </div>
        <textarea
          *ngIf="!showHtmlPreview"
          pInputTextarea
          class="w-full"
          [rows]="10"
          [(ngModel)]="bodyHtml"
        ></textarea>
        <iframe
          *ngIf="showHtmlPreview"
          [srcdoc]="bodyHtml"
          style="width: 100%; height: 20rem; border: 1px solid var(--surface-300); border-radius: 6px; background: white"
        ></iframe>
      </div>

      <div>
        <label class="block text-600 text-sm mb-1">Body (Plain Text Fallback)</label>
        <textarea pInputTextarea class="w-full" [rows]="4" [(ngModel)]="bodyText"></textarea>
      </div>
    </ng-container>

    <ng-template #smsFields>
      <div>
        <label class="block text-600 text-sm mb-1">Message <span class="text-red-500">*</span></label>
        <textarea pInputTextarea class="w-full" [rows]="4" [(ngModel)]="message"></textarea>
        <small [class.text-red-500]="smsOverLimit" class="text-500">
          {{ sms.length }} / {{ sms.limit }} characters<span *ngIf="sms.unicode">
            - contains special characters (emoji, accents or smart quotes), which lowers the limit to
            {{ sms.limit }}</span
          >
        </small>
      </div>
    </ng-template>
  </ng-template>

  <div *ngIf="isEmail">
    <label class="block text-600 text-sm mb-1">Attachments</label>
    <p-fileUpload
      mode="advanced"
      [multiple]="true"
      [showUploadButton]="false"
      [auto]="true"
      [customUpload]="true"
      (uploadHandler)="onFileSelect($event)"
      chooseLabel="Add Files"
    ></p-fileUpload>
    <small class="text-500">Up to 10MB per file. Executable files aren't allowed.</small>
    <div class="flex flex-column gap-2 mt-2" *ngIf="attachments.length">
      <div
        class="flex align-items-center justify-content-between surface-100 border-round p-2"
        *ngFor="let attachment of attachments; let i = index"
      >
        <span class="text-sm">
          <i class="pi pi-spin pi-spinner mr-2" *ngIf="attachment.uploading"></i>
          {{ attachment.original_filename }}
        </span>
        <p-button
          icon="pi pi-times"
          styleClass="p-button-text p-button-sm p-button-danger"
          (onClick)="removeAttachment(i)"
        ></p-button>
      </div>
    </div>
  </div>

  <div class="flex justify-content-end gap-2 border-top-1 surface-border pt-3 mt-2">
    <small class="text-500 align-self-center" *ngIf="uploadsPending">Waiting for attachments to finish uploading...</small>
    <p-button label="Cancel" styleClass="p-button-text" (onClick)="onCancel()"></p-button>
    <p-button
      *appHasPermission="['communication_logs.send_templated', 'communication_logs.send_freeform']"
      label="Send"
      icon="pi pi-send"
      styleClass="p-button-success"
      [disabled]="!isValid || sending"
      [loading]="sending"
      (onClick)="send()"
    ></p-button>
  </div>
</div>
